import type { DscBankSection } from "@credit-acceptance/step6-bff-client";

import { type BankAccountInput, makeEmptyBankAccount } from "./Signers.schema";

// ---------------------------------------------------------------------------
// bankMapping
//
// Maps the BFF `DscBankSection` (DscApplicantSection.bank) onto the form's
// single `BankAccountInput`. The form models ONE bank account per signer with
// independent account-type flags — matching dealer-ui's single-account model —
// so we select the first account from the BFF array and map its flags + bank
// name directly. Pure function — no React — so it stays unit-testable and
// reusable for both the Driver and 2nd Signer columns.
// ---------------------------------------------------------------------------

/**
 * Maps the applicant's `DscBankSection` onto the form's bank account. Uses the
 * first bank account in the array and maps its account-type flags and bank
 * name. Returns a blank bank account when none is present.
 * @param {DscBankSection | undefined} bank - The applicant's BFF bank section.
 * @returns {BankAccountInput} The hydrated bank account, or a blank one.
 */
export function mapBankSectionToBankAccount(bank?: DscBankSection): BankAccountInput {
  const account = bank?.bankAccounts?.[0];
  if (!account) {
    return makeEmptyBankAccount();
  }

  return {
    isSavingsAccount: Boolean(account.isSavingsAccount),
    isCheckingAccount: Boolean(account.isCheckingAccount),
    isPrepaidDebit: Boolean(account.isPrepaidDebit),
    bankName: account.bankName ?? "",
  };
}
